import { useEffect, useRef, useState } from 'react';
import { Camera, Download, Square } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { downloadCanvas } from '@/lib/utils';
import { toast } from 'sonner';

export function ScreenCapture() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [shot, setShot] = useState<string>('');

  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  async function start() {
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) {
      toast.error('Screen capture cancelled');
    }
  }

  function stop() {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
  }

  function capture() {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')?.drawImage(v, 0, 0);
    setShot(c.toDataURL('image/png'));
    toast.success('Screenshot taken');
  }

  return (
    <div>
      <ToolHeader
        toolId="screen-capture"
        title="Screen capture"
        description="Capture screenshots of your screen, window, or browser tab. PNG export."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          {!stream ? (
            <Button variant="primary" className="w-full" onClick={start}>
              <Camera size={12} />
              Start screen share
            </Button>
          ) : (
            <>
              <Button variant="primary" className="w-full" onClick={capture}>
                <Camera size={12} />
                Capture frame
              </Button>
              <Button variant="ghost" className="w-full" onClick={stop}>
                <Square size={12} />
                Stop sharing
              </Button>
            </>
          )}
          {shot && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                if (canvasRef.current) {
                  downloadCanvas(canvasRef.current, 'screenshot.png');
                }
              }}
            >
              <Download size={12} />
              Save PNG
            </Button>
          )}
          <div className="text-2xs text-fg-subtle pt-3 border-t border-border">
            Uses the browser's native screen-share API. The shared content is processed locally.
          </div>
        </Card>
        <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            {shot ? 'Captured' : 'Live'}
          </div>
          <div className="bg-bg-inset min-h-[420px] flex items-center justify-center p-3">
            {shot ? (
              <img src={shot} className="max-w-full max-h-[420px] rounded shadow-card" />
            ) : (
              <video
                ref={videoRef}
                className="max-w-full max-h-[420px] rounded"
                muted
                playsInline
              />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ScreenCapture;
